(() => {
  if (customElements.get('lena-select')) return;
  let sequence = 0;
  class LenaSelect extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.select = this.previousElementSibling;
      if (!(this.select instanceof HTMLSelectElement)) return;
      this.ready = true;
      this.uid = `lena-select-${++sequence}`;
      this.button = document.createElement('button');
      this.button.type = 'button';
      this.button.className = 'lena-select__trigger';
      this.button.id = `${this.uid}-trigger`;
      this.button.setAttribute('role', 'combobox');
      this.button.setAttribute('aria-haspopup', 'listbox');
      this.button.setAttribute('aria-expanded', 'false');
      this.button.setAttribute('aria-controls', `${this.uid}-list`);
      this.valueLabel = document.createElement('span');
      this.valueLabel.className = 'lena-select__value';
      this.button.append(this.valueLabel);
      this.menu = document.createElement('div');
      this.menu.id = `${this.uid}-list`;
      this.menu.className = 'lena-select__menu';
      this.menu.setAttribute('role', 'listbox');
      this.menu.setAttribute('popover', 'auto');
      this.append(this.button, this.menu);
      this.abort = new AbortController();
      const listen = (target, event, fn, options = {}) => target.addEventListener(event, fn, { ...options, signal: this.abort.signal });
      listen(this.button, 'click', () => this.opened ? this.close() : this.open());
      listen(this.button, 'keydown', event => this.onKey(event));
      listen(this.menu, 'keydown', event => this.onKey(event));
      listen(this.menu, 'pointerdown', event => event.preventDefault());
      listen(this.menu, 'click', event => {
        const option = event.target.closest('[data-option-index]');
        if (option && option.getAttribute('aria-disabled') !== 'true') this.choose(Number(option.dataset.optionIndex));
      });
      listen(this.menu, 'toggle', event => {
        this.opened = event.newState === 'open';
        this.button.setAttribute('aria-expanded', String(this.opened));
        if (!this.opened) this.button.removeAttribute('aria-activedescendant');
      });
      listen(this.select, 'change', () => this.sync());
      listen(this.select, 'invalid', event => { event.preventDefault(); this.button.focus(); this.button.setAttribute('aria-invalid', 'true'); });
      listen(window, 'resize', () => this.close());
      listen(document, 'scroll', event => { if (!this.menu.contains(event.target)) this.close(); }, { capture: true, passive: true });
      if (this.select.form) listen(this.select.form, 'reset', () => setTimeout(() => { if (this.isConnected) this.sync(); }, 0));
      for (const label of this.select.labels || []) listen(label, 'click', event => { event.preventDefault(); this.button.focus(); });
      this.observer = new MutationObserver(() => { this.close(); this.sync(); });
      this.observer.observe(this.select, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'selected', 'label', 'required', 'aria-invalid', 'aria-describedby'] });
      this.select.classList.add('lena-select-source');
      this.select.setAttribute('aria-hidden', 'true');
      this.select.tabIndex = -1;
      this.sync();
    }
    disconnectedCallback() {
      this.close();
      this.abort?.abort();
      this.observer?.disconnect();
      this.ready = false;
      this.replaceChildren();
    }
    sync() {
      this.valueLabel.textContent = this.select.selectedOptions[0]?.textContent.trim() || '';
      this.button.disabled = this.select.disabled;
      const label = this.select.getAttribute('aria-label') || [...new Set([...(this.select.labels || [])].map(item => item.textContent.trim()))].join(' ') || this.select.closest('sorting-filter-component')?.querySelector('.facets__label')?.textContent.trim() || this.select.name.replace(/[_\[\]]/g, ' ').trim();
      if (label) this.button.setAttribute('aria-label', `${label}: ${this.valueLabel.textContent}`);
      const labelledBy = this.select.getAttribute('aria-labelledby');
      if (labelledBy && !label) this.button.setAttribute('aria-labelledby', labelledBy);
      for (const attribute of ['aria-describedby', 'aria-invalid']) {
        const value = this.select.getAttribute(attribute);
        if (value !== null) this.button.setAttribute(attribute, value);
        else this.button.removeAttribute(attribute);
      }
      this.button.setAttribute('aria-required', String(this.select.required));
      this.menu.setAttribute('aria-label', label || this.valueLabel.textContent);
    }
    isDisabled(option) {
      return option.disabled || (option.parentElement instanceof HTMLOptGroupElement && option.parentElement.disabled);
    }
    open() {
      if (this.select.disabled || !this.select.options.length) return;
      this.sync();
      this.menu.replaceChildren();
      [...this.select.options].forEach((option, index) => {
        const item = document.createElement('div');
        item.id = `${this.uid}-option-${index}`;
        item.dataset.optionIndex = String(index);
        item.className = 'lena-select__option';
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', String(option.selected));
        item.setAttribute('aria-disabled', String(this.isDisabled(option)));
        if (option.lang) item.lang = option.lang;
        item.textContent = option.textContent.trim();
        this.menu.append(item);
      });
      const rect = this.button.getBoundingClientRect();
      const below = window.innerHeight - rect.bottom - 12;
      const above = rect.top - 12;
      const upward = below < 200 && above > below;
      const height = Math.max(80, Math.min(300, upward ? above : below));
      const width = Math.min(Math.max(rect.width, 200), window.innerWidth - 24);
      Object.assign(this.menu.style, { width: `${width}px`, maxHeight: `${height}px`, left: `${Math.max(12, Math.min(rect.left, window.innerWidth - width - 12))}px`, top: upward ? 'auto' : `${rect.bottom + 6}px`, bottom: upward ? `${window.innerHeight - rect.top + 6}px` : 'auto' });
      this.menu.showPopover();
      this.opened = true;
      this.button.setAttribute('aria-expanded', 'true');
      this.activate(this.select.selectedIndex >= 0 ? this.select.selectedIndex : 0);
    }
    activate(index) {
      this.activeIndex = index;
      for (const item of this.menu.children) item.classList.toggle('is-active', Number(item.dataset.optionIndex) === index);
      const active = this.menu.querySelector(`[data-option-index="${index}"]`);
      if (active) {
        this.button.setAttribute('aria-activedescendant', active.id);
        const top = active.offsetTop;
        if (top < this.menu.scrollTop) this.menu.scrollTop = top;
        else if (top + active.offsetHeight > this.menu.scrollTop + this.menu.clientHeight) this.menu.scrollTop = top + active.offsetHeight - this.menu.clientHeight;
      }
    }
    choose(index) {
      if (!this.select.options[index] || this.isDisabled(this.select.options[index])) return;
      const changed = this.select.selectedIndex !== index;
      this.select.selectedIndex = index;
      this.close();
      this.sync();
      this.button.focus({ preventScroll: true });
      if (changed) {
        this.select.dispatchEvent(new Event('input', { bubbles: true }));
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    close() {
      if (!this.opened) return;
      if (this.menu?.isConnected && this.menu.matches(':popover-open')) this.menu.hidePopover();
      this.opened = false;
      this.button.setAttribute('aria-expanded', 'false');
      this.button.removeAttribute('aria-activedescendant');
    }
    onKey(event) {
      if (event.key === 'Tab') { this.close(); return; }
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); this.close(); return; }
      if (['ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter', ' '].includes(event.key)) {
        event.preventDefault();
        event.stopPropagation();
        if (!this.opened) { this.open(); return; }
        if (event.key === 'Enter' || event.key === ' ') { this.choose(this.activeIndex); return; }
        const enabled = [...this.select.options].map((option, index) => this.isDisabled(option) ? -1 : index).filter(index => index >= 0);
        if (!enabled.length) return;
        const position = Math.max(0, enabled.indexOf(this.activeIndex));
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? enabled.length - 1 : Math.min(enabled.length - 1, Math.max(0, position + (event.key === 'ArrowDown' ? 1 : -1)));
        this.activate(enabled[next]);
      } else if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
        event.preventDefault();
        event.stopPropagation();
        if (!this.opened) this.open();
        const now = Date.now();
        this.search = now - (this.lastKey || 0) < 650 ? (this.search || '') + event.key : event.key;
        this.lastKey = now;
        const index = [...this.select.options].findIndex(option => !this.isDisabled(option) && option.textContent.trim().toLocaleLowerCase().startsWith(this.search.toLocaleLowerCase()));
        if (index >= 0) this.activate(index);
      }
    }
  }
  customElements.define('lena-select', LenaSelect);
  if (!HTMLElement.prototype.showPopover) return;
  const selector = 'select:not([multiple]):not([data-native-select])';
  const enhance = root => {
    const selects = root instanceof HTMLSelectElement ? [root] : root.querySelectorAll?.(selector) || [];
    for (const select of selects) {
      if (!select.matches(selector) || select.size > 1 || select.closest('iframe') || select.nextElementSibling?.matches('lena-select')) continue;
      select.after(document.createElement('lena-select'));
    }
  };
  enhance(document);
  let refreshPending = false;
  new MutationObserver(records => {
    let externalChange = false;
    for (const record of records) {
      if (record.target instanceof Element && !record.target.closest('lena-select')) externalChange = true;
    }
    for (const record of records) for (const node of record.addedNodes) {
      if (!(node instanceof Element) || node.closest('lena-select')) continue;
      enhance(node);
    }
    if (externalChange && !refreshPending) {
      refreshPending = true;
      requestAnimationFrame(() => { refreshPending = false; enhance(document); });
    }
  }).observe(document.body, { childList: true, subtree: true });
})();
